import {
  AnalisiDto,
  BonificaDto,
  FiltrazioneDto,
  ManutenzioneCalendarioDto,
  MiniNodoDto,
  NodoDto,
  useGetAnalisi,
  useGetBonifiche,
  useGetFiltrazioni,
  useGetManutenzioni,
} from "@/api";
import AnalisiIcon from "@/assets/analisi.png";
import BonificaIcon from "@/assets/bonifica.png";
import FiltroIcon from "@/assets/filtro.png";
import { useAuth } from "@/context/useAuth";
import Box from "@/elements/Box";
import Typography from "@/elements/Typography";
import { PosizioneFilter } from "@/hooks/usePosizioneFilter";
import { usePosizioneFilterWithNavigation } from "@/hooks/usePosizioneFilterWithNavigation";
import { portaleConfig } from "@/portaliConfig";
import { getCalendarMessages } from "@/utils/calendarioUtils";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import {
  Autocomplete,
  Checkbox,
  Icon,
  TextField,
  Tooltip,
} from "@mui/material";
import { merge } from "lodash";
import moment, { Moment } from "moment";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Calendar, momentLocalizer, View, Views } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { useNavigate } from "react-router-dom";
import Loading from "../Loading";
import { NodoPicker } from "../NodoPicker";
import PosizioneNodo from "../PosizioneNodo";
import "./calendar.css";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const localizer = momentLocalizer(moment);

const views = [Views.MONTH, Views.AGENDA];

const allDayAccessor = () => true;

type Evento = {
  date: Moment;
  nodo: NodoDto | null;
} & (
  | {
      type: "analisi" | "analisi_scadenza";
      analisi: AnalisiDto;
    }
  | {
      type: "filtrazione" /*| "filtrazione_scadenza"*/;
      filtrazione: FiltrazioneDto;
    }
  | {
      type: "bonifica" | "bonifica_scadenza";
      bonifica: BonificaDto;
    }
  | {
      type: "manutenzione" | "manutenzione_scadenza";
      manutenzione: ManutenzioneCalendarioDto;
    }
);

type EventoType = Evento["type"];

const colorsByEvento: {
  [type in EventoType]: {
    backgroundColor: string;
    color?: string;
    blinking?: boolean;
  };
} = {
  analisi: {
    backgroundColor: "#f8b31e",
  },
  analisi_scadenza: {
    backgroundColor: "#f8b31e",
    blinking: true,
  },
  filtrazione: {
    backgroundColor: "#877a9b",
    color: "#E0E0E0",
  },
  // filtrazione_scadenza: {
  //   backgroundColor: "#877a9b",
  //   color: "#E0E0E0",
  //   blinking: true,
  // },
  bonifica: {
    backgroundColor: "#885c56",
    color: "#E0E0E0",
  },
  bonifica_scadenza: {
    backgroundColor: "#885c56",
    color: "#E0E0E0",
    blinking: true,
  },
  manutenzione: {
    backgroundColor: "#1f6a8a",
    color: "#E0E0E0",
  },
  manutenzione_scadenza: {
    backgroundColor: "#1f6a8a",
    color: "#E0E0E0",
    blinking: true,
  },
};

function CalendarioInterventiPage() {
  const { t } = useTranslation();
  const calendarMessages = useMemo(() => getCalendarMessages(t), [t]);

  const [filterTypes, setFilterTypes] = useState<EventoType[] | null>(null);

  const { filters, filtersLoading, changeFilter } =
    usePosizioneFilterWithNavigation("/calendario-interventi", {
      enableNodo: true,
      enableDate: true,
    });

  const date = useMemo(() => {
    return filters.date ?? moment().startOf("month").toDate();
  }, [filters.date]);

  const onNavigate = useCallback((date: Date) => {
    changeFilter("date")(null, moment(date).startOf("month").toDate());
  }, []);

  const { user, hasPermission } = useAuth();

  const { data: analisi, isLoading: analisiLoading } = useGetAnalisi(
    portaleConfig.id,
    {
      from: moment(date).startOf("month").toISOString(),
      to: moment(date).endOf("month").toISOString(),
    },
    {
      query: {
        enabled: hasPermission("AnalisiListaRead"),
      },
    }
  );
  const { data: filtrazioni, isLoading: filtrazioniLoading } =
    useGetFiltrazioni(
      portaleConfig.id,
      {
        from: moment(date).startOf("month").toISOString(),
        to: moment(date).endOf("month").toISOString(),
      },
      {
        query: {
          enabled: hasPermission("FiltrazioniListaRead"),
        },
      }
    );
  const { data: bonifiche, isLoading: bonificheLoading } = useGetBonifiche(
    portaleConfig.id,
    {
      from: moment(date).startOf("month").toISOString(),
      to: moment(date).endOf("month").toISOString(),
    },
    {
      query: {
        enabled: hasPermission("BonificheListaRead"),
      },
    }
  );
  const { data: manutenzioni, isLoading: manutenzioniLoading } =
    useGetManutenzioni(
      portaleConfig.id,
      {
        from: moment(date).startOf("month").toISOString(),
        to: moment(date).endOf("month").toISOString(),
      },
      {
        query: {
          enabled: hasPermission("CalendarioInterventiRead"),
        },
      }
    );

  const loading =
    filtersLoading ||
    analisiLoading ||
    filtrazioniLoading ||
    bonificheLoading ||
    manutenzioniLoading;

  const [view, setView] = useState<View>(views[0]);

  const eventsAnalisi = useMemo(() => {
    const events: Evento[] = [];

    const includiEffettuate = !filterTypes || filterTypes.includes("analisi");
    const includiScadenze =
      !filterTypes || filterTypes.includes("analisi_scadenza");
    if (analisi?.data) {
      for (const a of analisi.data) {
        for (const n of a.nodi ?? []) {
          if (includiEffettuate && filterIncludesPaths(filters, n)) {
            events.push({
              type: "analisi",
              date: moment(a.data, "YYYY-MM-DD").startOf("day"),
              analisi: a,
              nodo: n,
            });
          }

          if (includiScadenze && filterIncludesPaths(filters, n)) {
            const isUltimaAnalisi = n.ultimaAnalisiId === a.id;
            if (isUltimaAnalisi) {
              events.push({
                type: "analisi_scadenza",
                date: moment(a.validaFinoA).startOf("day"),
                analisi: a,
                nodo: n,
              });
            }
          }
        }
      }
    }

    return events;
  }, [analisi, filters, filterTypes]);

  const eventsFiltrazioni = useMemo(() => {
    const events: Evento[] = [];

    const includiEffettuate =
      !filterTypes || filterTypes.includes("filtrazione");

    if (filtrazioni?.data) {
      for (const f of filtrazioni.data) {
        if (!f.nodo) {
          continue;
        }

        if (includiEffettuate && filterIncludesPaths(filters, f.nodo)) {
          events.push({
            type: "filtrazione",
            date: moment(f.data, "YYYY-MM-DD").startOf("day"),
            filtrazione: f,
            nodo: f.nodo,
          });
        }

        // if (includiScadenze) {
        //   events.push({
        //     type: "filtrazione_scadenza",
        //     date: moment(f.).startOf("day"),
        //     filtrazione: f,
        //     azienda: azienda,
        //     centrale: centrale,
        //   });
        // }
      }
    }

    return events;
  }, [filtrazioni, filters, filterTypes]);

  const eventsBonifiche = useMemo(() => {
    const events: Evento[] = [];

    const includiEffettuate = !filterTypes || filterTypes.includes("bonifica");
    const includiScadenze =
      !filterTypes || filterTypes.includes("bonifica_scadenza");
    if (bonifiche?.data) {
      for (const b of bonifiche.data) {
        if (!b.nodo) {
          continue;
        }

        if (includiEffettuate && filterIncludesPaths(filters, b.nodo)) {
          events.push({
            type: "bonifica",
            date: moment(b.data, "YYYY-MM-DD").startOf("day"),
            bonifica: b,
            nodo: b.nodo,
          });
        }

        if (
          includiScadenze &&
          b.scadenza &&
          filterIncludesPaths(filters, b.nodo)
        ) {
          const isUltimaBonifica = b.nodo.ultimaBonificaId === b.id;
          if (isUltimaBonifica) {
            events.push({
              type: "bonifica_scadenza",
              date: moment(b.scadenza).startOf("day"),
              bonifica: b,
              nodo: b.nodo,
            });
          }
        }
      }
    }

    return events;
  }, [bonifiche, filters, filterTypes]);

  const eventsManutenzioni = useMemo(() => {
    const events: Evento[] = [];

    const includiEffettuate =
      !filterTypes || filterTypes.includes("manutenzione");
    const includiScadenze =
      !filterTypes || filterTypes.includes("manutenzione_scadenza");

    if (manutenzioni?.data) {
      for (const m of manutenzioni.data) {
        if (!m.nodo) {
          continue;
        }

        if (includiEffettuate && filterIncludesPaths(filters, m.nodo)) {
          events.push({
            type: "manutenzione",
            date: moment(m.data, "YYYY-MM-DD").startOf("day"),
            manutenzione: m,
            nodo: m.nodo,
          });
        }

        if (
          includiScadenze &&
          m.scadenza &&
          filterIncludesPaths(filters, m.nodo)
        ) {
          const isUltimaManutenzioneConScadenza =
            m.nodo.ultimaManutenzioneConScadenzaId === m.id;

          if (isUltimaManutenzioneConScadenza) {
            events.push({
              type: "manutenzione_scadenza",
              date: moment(m.scadenza, "YYYY-MM-DD").startOf("day"),
              manutenzione: m,
              nodo: m.nodo,
            });
          }
        }
      }
    }

    return events;
  }, [manutenzioni, filters, filterTypes]);

  const events = useMemo(() => {
    return [
      ...eventsAnalisi,
      ...eventsFiltrazioni,
      ...eventsBonifiche,
      ...eventsManutenzioni,
    ];
  }, [eventsAnalisi, eventsFiltrazioni, eventsBonifiche, eventsManutenzioni]);

  const renderTitle = useMemo(() => {
    const TitoloEvento = ({ evento }: { evento: Evento }) => {
      const iconSize = 24;

      let icon: string | null;
      let iconName: string | null = null;
      let tooltip: string;
      let nome: string;
      let isScadenza: boolean;
      const { color } = colorsByEvento[evento.type];

      if (evento.type === "analisi" || evento.type === "analisi_scadenza") {
        icon = AnalisiIcon;
        tooltip =
          evento.type === "analisi"
            ? t("calendarioInterventi.eventi.analisiSvolta")
            : t("calendarioInterventi.eventi.analisiScadenza");
        isScadenza = evento.type === "analisi_scadenza";
        nome =
          evento.nodo?.nome ?? t("calendarioInterventi.eventi.analisiCampione");
      } else if (
        evento.type === "filtrazione"
        //|| evento.type === "filtrazione_scadenza"
      ) {
        icon = FiltroIcon;
        tooltip =
          evento.type === "filtrazione"
            ? t("calendarioInterventi.eventi.filtrazioneSvolta")
            : t("calendarioInterventi.eventi.filtrazioneScadenza");
        isScadenza = false; // evento.type === "filtrazione_scadenza";
        nome = evento.nodo?.nome ?? "";
      } else if (
        evento.type === "bonifica" ||
        evento.type === "bonifica_scadenza"
      ) {
        icon = BonificaIcon;
        tooltip =
          evento.type === "bonifica"
            ? t("calendarioInterventi.eventi.bonificaSvolta")
            : t("calendarioInterventi.eventi.bonificaScadenza");
        isScadenza = evento.type === "bonifica_scadenza";
        nome = evento.nodo?.nome ?? "";
      } else if (
        evento.type === "manutenzione" ||
        evento.type === "manutenzione_scadenza"
      ) {
        icon = null;
        iconName = "build";
        tooltip =
          evento.type === "manutenzione"
            ? t("calendarioInterventi.eventi.manutenzioneSvolta")
            : t("calendarioInterventi.eventi.manutenzioneScadenza");
        isScadenza = evento.type === "manutenzione_scadenza";
        nome = evento.nodo?.nome ?? "";
      } else {
        return <Box>{t("calendarioInterventi.eventi.sconosciuto")}</Box>;
      }

      return (
        <Tooltip title={tooltip}>
          <Box display="flex" flexDirection="column">
            <Box display="flex" flexDirection="row" alignItems="center">
              <Box>
                {icon ? (
                  <img src={icon} width={iconSize} alt={tooltip} />
                ) : (
                  <Icon sx={{ color }}>{iconName}</Icon>
                )}
              </Box>
              {isScadenza && <Icon sx={{ color, mr: 0.5 }}>schedule</Icon>}
              {evento.nodo && view === Views.AGENDA ? (
                <PosizioneNodo
                  nodo={evento.nodo}
                  showUltimoNodo
                  big
                  color={color}
                />
              ) : (
                <Typography sx={{ color }}>{nome}</Typography>
              )}
            </Box>

            {evento.nodo?.breadcrumbs && (
              <Box display="flex" flexDirection="row" gap={0.5}>
                <Icon sx={{ color }}>factory</Icon>
                <Typography sx={{ color }}>
                  {evento.nodo.tipo === "Azienda"
                    ? evento.nodo.nome
                    : evento.nodo.breadcrumbs[0]?.nodo?.nome}
                </Typography>
              </Box>
            )}
          </Box>
        </Tooltip>
      );
    };

    return (e: any) => (<TitoloEvento evento={e as Evento} />) as any;
  }, [t, view]);

  const navigate = useNavigate();

  function handleEventSelect(evento: Evento) {
    if (evento.nodo) {
      navigate(`/nodi/${evento.nodo.id}`);
    } else if (user?.isEsterno) {
      navigate(`/home`);
    }
  }

  return (
    <Box
      p={2}
      sx={{
        height: "calc(100vh - 96px)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        display="flex"
        flexWrap="wrap"
        alignItems="flex-start"
        justifyContent="space-between"
      >
        <Typography variant="h4" mr={2}>
          {t("calendarioInterventi.titolo")}
        </Typography>

        <Box display="flex" gap={1} pt={2} flexWrap="wrap">
          <EventoTypePicker value={filterTypes} onChange={setFilterTypes} />
        </Box>
      </Box>

      <NodoPicker
        grow="horizontally"
        value={filters.nodo}
        onChange={(x) => changeFilter("nodo")(null, x)}
      />

      <Box
        mt={4}
        mb={0}
        display="flex"
        flexDirection="row"
        flexWrap="wrap"
        gap={2}
        flex={1}
        sx={{
          position: "relative",
        }}
      >
        <Box
          sx={{
            width: "100%",
            opacity: loading ? 0.25 : 1,
            "& .rbc-agenda-table .rbc-header:nth-child(2)": { display: "none" },
            "& .rbc-agenda-table .rbc-agenda-time-cell": { display: "none" },
          }}
        >
          <Calendar
            messages={calendarMessages}
            views={views}
            localizer={localizer}
            events={events}
            startAccessor="date"
            endAccessor="date"
            titleAccessor={renderTitle}
            allDayAccessor={allDayAccessor}
            eventPropGetter={eventPropGetter}
            //resourceIdAccessor={getAccessor("getId", accessors)}
            //eventPropGetter={getAccessor0_3("getEventProps", accessors)}
            popup
            view={view as View}
            onView={(v) => setView(v)}
            onSelectEvent={handleEventSelect}
            date={date}
            onNavigate={onNavigate}
          />
        </Box>
        {loading && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Loading />
          </Box>
        )}
      </Box>
    </Box>
  );
}

const eventPropGetter = (evento: Evento) => {
  const colors = colorsByEvento[evento.type];
  return {
    className: colors.blinking ? "calendar-blinking-event" : undefined,
    style: {
      backgroundColor: colors.backgroundColor,
      color: colors.color,
    },
  };
};

function EventoTypePicker({
  value,
  onChange,
}: {
  value: EventoType[] | null;
  onChange: (newValues: EventoType[] | null) => void;
}) {
  const { t } = useTranslation();

  const eventiLabelByType = useMemo(
    () => ({
      analisi: t("calendarioInterventi.filtri.analisi"),
      analisi_scadenza: t("calendarioInterventi.filtri.analisiScadenza"),
      filtrazione: t("calendarioInterventi.filtri.filtrazione"),
      bonifica: t("calendarioInterventi.filtri.bonifica"),
      bonifica_scadenza: t("calendarioInterventi.filtri.bonificaScadenza"),
      manutenzione: t("calendarioInterventi.filtri.manutenzione"),
      manutenzione_scadenza: t(
        "calendarioInterventi.filtri.manutenzioneScadenza"
      ),
    }),
    [t]
  );

  const eventiTypeOptions = useMemo(
    () =>
      Object.entries(eventiLabelByType).map(([key, label]) => {
        return {
          value: key as EventoType,
          label,
        };
      }),
    [eventiLabelByType]
  );

  const _value = useMemo(() => {
    return (
      value?.map((v) => eventiTypeOptions.find((x) => x.value === v)!) ?? null
    );
  }, [eventiTypeOptions, value]);

  const allSelected = _value && _value.length === eventiTypeOptions.length;

  const textValue = useMemo(() => {
    if (!_value) {
      return "";
    } else if (allSelected) {
      return "";
    }
    const labels = eventiTypeOptions
      .filter((option) => _value.includes(option))
      .map((o) => o.label);
    return labels.join(", ");
  }, [_value, allSelected, eventiTypeOptions]);

  return (
    <Autocomplete
      multiple
      options={eventiTypeOptions}
      size="small"
      renderOption={(props, option, { selected }) => {
        const { key, ...optionProps } = props as any;
        return (
          <li key={key} {...optionProps}>
            <Checkbox
              icon={icon}
              checkedIcon={checkedIcon}
              style={{ marginRight: 8 }}
              checked={selected}
            />
            {option.label}
          </li>
        );
      }}
      renderInput={(params) => {
        return (
          <TextField
            {...merge(params, { inputProps: { value: textValue } })}
            sx={{
              "& .MuiChip-root": { display: "none" },
            }}
            placeholder={t("calendarioInterventi.filtri.placeholder")}
          />
        );
      }}
      value={_value ?? []}
      onChange={(_, newValues) => {
        const selected = newValues.map((o) => o.value) as EventoType[];
        onChange(selected.length > 0 ? selected : null);
      }}
    />
  );
}

export default CalendarioInterventiPage;

function filterIncludesPaths(filters: PosizioneFilter, nodo: NodoDto) {
  if (!filters.nodo?.breadcrumbs) {
    return true;
  }

  const path: (NodoDto | MiniNodoDto)[] = [
    ...filters.nodo.breadcrumbs.map((x) => x.nodo),
    filters.nodo,
  ];

  let pathInclusa = true;
  for (let i = 0; i < path.length; i++) {
    const filterCrumb = path[i];
    const nodoCrumb = nodo.breadcrumbs?.[i];

    if (!nodoCrumb) {
      // il filtro è più specifico della path di questo nodo, quindi questa path non è inclusa
      pathInclusa = false;
      break;
    } else if (nodoCrumb.nodo.id !== filterCrumb.id) {
      pathInclusa = false;
      break;
    } else {
      // la path è valida fino a qui, continuiamo a iterare
    }
  }

  if (pathInclusa) {
    return true;
  }

  return false;
}
